import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, MessageCircle } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { notify } from "@/lib/toast";
import { logError, logWarn } from "@/lib/logger";
import { HTTP_NOT_FOUND } from "@/lib/constants";

async function loadOwnerBySlug(slug) {
    try {
        const { data } = await api.get(`/qr/public/${slug}`);
        return data;
    } catch (e) {
        if (e?.response?.status !== HTTP_NOT_FOUND) logError("Scanner load failed", e);
        return null;
    }
}

function recordScan(slug) {
    api.post(`/qr/scan/${slug}`).catch((e) => logWarn("Scan record failed", e));
}

export default function Scanner() {
    const { slug } = useParams();
    const [owner, setOwner] = useState(null);
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let cancelled = false;
        loadOwnerBySlug(slug).then((data) => {
            if (cancelled) return;
            if (!data) { setNotFound(true); return; }
            setOwner(data);
            recordScan(slug);
        });
        return () => { cancelled = true; };
    }, [slug]);

    async function onSubmit(e) {
        e.preventDefault();
        if (!owner) return;
        setLoading(true);
        try {
            const { data } = await api.post(`/qr/lead/${slug}`, {
                name: form.name.trim(),
                phone: form.phone,
                email: form.email.trim(),
                message: form.message,
            });
            setSubmitted({ whatsapp_url: data.whatsapp_url });
            notify("Opening WhatsApp...");
            setTimeout(() => { window.open(data.whatsapp_url, "_blank"); }, 500);
        } catch (err) {
            notify(formatErr(err), "error");
        } finally {
            setLoading(false);
        }
    }

    function onChange(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    return (
        <section className="min-h-screen flex items-center justify-center relative mesh-gradient py-16" data-testid="scanner-page">
            <div className="max-w-md w-full mx-4">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-whats-green to-brand-600 flex items-center justify-center">
                        <MessageCircle className="w-8 h-8 text-white" />
                    </div>
                    <ScannerHeader notFound={notFound} owner={owner} />
                </div>

                {owner && !submitted && (
                    <div className="glass rounded-3xl p-8">
                        <form onSubmit={onSubmit} className="space-y-5">
                            <Field label="Your Name *">
                                <input data-testid="scan-name" required value={form.name} onChange={(e) => onChange("name", e.target.value)}
                                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="Enter your name" />
                            </Field>
                            <Field label="Your Phone *">
                                <input data-testid="scan-phone" type="tel" required value={form.phone} onChange={(e) => onChange("phone", e.target.value)}
                                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="e.g., 9876543210" />
                            </Field>
                            <Field label="Email (optional)">
                                <input data-testid="scan-email" type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)}
                                    className="input-field w-full px-4 py-3 rounded-xl" placeholder="your@email.com" />
                            </Field>
                            <Field label="Message">
                                <textarea data-testid="scan-message" value={form.message} onChange={(e) => onChange("message", e.target.value)}
                                    rows={2} className="input-field w-full px-4 py-3 rounded-xl text-sm resize-none"
                                    placeholder="Hi, I'd like to connect..." />
                            </Field>
                            <button data-testid="scan-submit" type="submit" disabled={loading}
                                className="whatsapp-btn w-full py-4 rounded-xl text-lg font-bold text-white flex items-center justify-center gap-3 disabled:opacity-50">
                                <MessageCircle className="w-6 h-6" />
                                {loading ? "Connecting..." : "Connect on WhatsApp"}
                            </button>
                        </form>
                        <div className="mt-6 text-center text-xs text-gray-500 flex items-center justify-center gap-1">
                            <Lock className="w-4 h-4" /> Your data is secure and only shared with the QR owner
                        </div>
                    </div>
                )}

                {submitted && (
                    <div className="glass rounded-3xl p-8 text-center" data-testid="scan-success">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-500/20 flex items-center justify-center">
                            <MessageCircle className="w-8 h-8 text-brand-400" />
                        </div>
                        <h2 className="font-display font-bold text-2xl mb-2">Opening WhatsApp...</h2>
                        <p className="text-gray-400 text-sm mb-6">If WhatsApp didn't open, tap below.</p>
                        <a href={submitted.whatsapp_url} target="_blank" rel="noreferrer" className="whatsapp-btn inline-flex px-8 py-3 rounded-xl font-bold text-white gap-2">
                            <MessageCircle className="w-5 h-5" /> Open WhatsApp
                        </a>
                    </div>
                )}
            </div>
        </section>
    );
}

function ScannerHeader({ notFound, owner }) {
    if (notFound) {
        return (
            <>
                <h1 className="font-display font-bold text-2xl mb-2">QR Not Found</h1>
                <p className="text-gray-400 text-sm">This QR code is no longer valid.</p>
            </>
        );
    }
    if (owner) {
        return (
            <>
                <h1 className="font-display font-bold text-2xl mb-2" data-testid="scanner-title">Connect with {owner.business || owner.name}</h1>
                <p className="text-gray-400 text-sm">Enter your details to connect on WhatsApp</p>
            </>
        );
    }
    return <p className="text-gray-500">Loading...</p>;
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
            {children}
        </div>
    );
}
